import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ACTUAL_BAR_COLOR, OPTIMIZED_BAR_COLOR, getRiskOverviewChartChrome, getRiskOverviewTooltipStyle, riskOverviewSecondaryControlSx } from '../../utils/riskOverviewStyles';
import { getChartExtent } from '../../utils/riskOverviewChartPresentation';
import { numberText } from '../../utils/portfolioDesign';
import { posSizingTableTitleSx } from '../pos_sizing_table/tableStyles';

const SERIES = [
    { key: 'baseline', label: 'Baseline', color: ACTUAL_BAR_COLOR },
    { key: 'optimized', label: 'Optimized', color: OPTIMIZED_BAR_COLOR },
];

function ExposureTooltip({ active, payload, label, theme }) {
    if (!active || !payload?.length) return null;
    return <Box role="tooltip" style={getRiskOverviewTooltipStyle(theme)}>
        <Typography variant="caption" fontWeight={600}>{label}</Typography>
        {SERIES.map(({ key, label: seriesLabel, color }) => {
            const value = payload.find((entry) => entry.dataKey === key)?.value;
            return <Stack key={key} direction="row" justifyContent="space-between" gap={3} sx={{ color }}>
                <Typography variant="caption">{seriesLabel}</Typography>
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {Number.isFinite(value) ? `${numberText(value)}%` : '—'}
                </Typography>
            </Stack>;
        })}
        <Typography variant="caption" color="text.secondary">Exposure · % of AUM</Typography>
    </Box>;
}

export default function OptimizerFactorExposureChart({ baseline, selected }) {
    const theme = useTheme();
    const chrome = getRiskOverviewChartChrome(theme);
    const [sortKey, setSortKey] = useState('baseline');
    const [sortOrder, setSortOrder] = useState('desc');
    const rows = useMemo(() => (baseline?.factor_names ?? []).map((name, index) => ({
        name,
        baseline: Number.isFinite(baseline.factor_exposures_pct_aum?.[index]) ? baseline.factor_exposures_pct_aum[index] : null,
        optimized: Number.isFinite(selected.factor_exposures_pct_aum?.[index]) ? selected.factor_exposures_pct_aum[index] : null,
    })).sort((a, b) => {
        const nameOrder = a.name.localeCompare(b.name);
        if (sortKey === 'name') return sortOrder === 'asc' ? nameOrder : -nameOrder;
        if (a[sortKey] == null || b[sortKey] == null) return Number(a[sortKey] == null) - Number(b[sortKey] == null) || nameOrder;
        const magnitude = Math.abs(a[sortKey]) - Math.abs(b[sortKey]);
        return (sortOrder === 'asc' ? magnitude : -magnitude) || nameOrder;
    }), [baseline, selected, sortKey, sortOrder]);
    const extent = getChartExtent(rows.map((row) => ({ value: row.baseline, optValue: row.optimized })), true);
    const orderLabel = sortKey === 'name'
        ? (sortOrder === 'asc' ? 'A–Z' : 'Z–A')
        : (sortOrder === 'desc' ? 'Largest first' : 'Smallest first');

    return <Box component="section" aria-label="Factor exposure comparison" sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 1 }}>
            <Box>
                <Typography component="h3" variant="subtitle1" sx={posSizingTableTitleSx}>Factor exposures</Typography>
                <Typography variant="caption" color="text.secondary">Signed exposure · % of AUM</Typography>
            </Box>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} role="group" aria-label="Factor sorting">
                <FormControl size="small" sx={{ minWidth: 235 }}>
                    <InputLabel id="optimizer-factor-sort-label">Sort by</InputLabel>
                    <Select labelId="optimizer-factor-sort-label" label="Sort by" value={sortKey}
                        onChange={(event) => { setSortKey(event.target.value); setSortOrder(event.target.value === 'name' ? 'asc' : 'desc'); }}>
                        <MenuItem value="baseline">Absolute baseline exposure</MenuItem>
                        <MenuItem value="optimized">Absolute optimized exposure</MenuItem>
                        <MenuItem value="name">Factor name</MenuItem>
                    </Select>
                </FormControl>
                <Button size="small" aria-label="Reverse factor sort order" sx={riskOverviewSecondaryControlSx}
                    onClick={() => setSortOrder((order) => order === 'asc' ? 'desc' : 'asc')}
                    endIcon={sortOrder === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}>
                    {orderLabel}
                </Button>
            </Stack>
        </Stack>
        <Stack direction="row" gap={2} sx={{ mb: 1.5 }}>
            {SERIES.map(({ key, label, color }) => <Stack key={key} direction="row" alignItems="center" gap={0.75}>
                <Box sx={{ width: 12, height: 12, bgcolor: color }} /><Typography variant="caption">{label}</Typography>
            </Stack>)}
        </Stack>
        {!rows.length ? <Alert severity="info">No factor exposures are available for this portfolio.</Alert>
            : <Box role="region" aria-label="Scrollable factor exposures" tabIndex={0}
                sx={{ maxHeight: 440, overflowY: 'auto', scrollbarGutter: 'stable', overscrollBehaviorY: 'contain' }}>
                <Box sx={{ height: Math.max(220, rows.length * 30 + 40) }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={rows} layout="vertical" margin={{ left: 10, right: 24, top: 8, bottom: 8 }}
                            barSize={16} barCategoryGap={0} barGap={-12}>
                            <CartesianGrid stroke={chrome.grid ?? theme.palette.divider} strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" domain={[extent.min < 0 ? 'auto' : 0, extent.max > 0 ? 'auto' : (extent.min < 0 ? 0 : 1)]}
                                tickFormatter={(value) => `${numberText(value, 1)}%`} tick={{ fill: chrome.tick, fontSize: chrome.tickFontSize }}
                                axisLine={{ stroke: chrome.axis }} tickLine={false} />
                            <YAxis dataKey="name" type="category" width={140} interval={0} axisLine={false} tickLine={false}
                                tick={{ fill: chrome.tick, fontSize: chrome.tickFontSize }} />
                            <ReferenceLine x={0} stroke={chrome.reference} />
                            <Tooltip content={<ExposureTooltip theme={theme} />} wrapperStyle={{ pointerEvents: 'none' }}
                                cursor={{ fill: chrome.cursor ?? alpha(theme.palette.text.primary, 0.04) }} />
                            <Bar dataKey="baseline" name="Baseline" fill={ACTUAL_BAR_COLOR} fillOpacity={0.4} isAnimationActive={false} />
                            <Bar dataKey="optimized" name="Optimized" fill={OPTIMIZED_BAR_COLOR} fillOpacity={0.5} barSize={8} isAnimationActive={false} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>}
    </Box>;
}
